import { useState } from 'react';
import Container from '@cloudscape-design/components/container';
import Header from '@cloudscape-design/components/header';
import Box from '@cloudscape-design/components/box';
import Button from '@cloudscape-design/components/button';
import Badge from '@cloudscape-design/components/badge';
import ProgressBar from '@cloudscape-design/components/progress-bar';
import SpaceBetween from '@cloudscape-design/components/space-between';
import Table from '@cloudscape-design/components/table';
import StatusIndicator from '@cloudscape-design/components/status-indicator';
import Modal from '@cloudscape-design/components/modal';
import FormField from '@cloudscape-design/components/form-field';
import Textarea from '@cloudscape-design/components/textarea';
import Link from '@cloudscape-design/components/link';
import { UserLink } from '../../components/UserLink';
import { useQuery, useQueryClient, useQueries } from '@tanstack/react-query';
import { mentorsApi, MenteeWithProgress } from '../../api/mentors';
import { activitiesApi, CohortActivity, ActivityCompletion } from '../../api/activities';
import { queryKeys } from '../../lib/queryClient';
import { LoadingSpinner } from '../../components/LoadingSpinner';
import { ErrorAlert } from '../../components/ErrorAlert';
import { useNotifications } from '../../components/NotificationFlashbar';

interface MyMenteesTabProps {
  cohortId: string;
  mentorId: string;
}

interface ActivityWithStatus extends CohortActivity {
  completion?: ActivityCompletion;
  status: 'not_started' | 'in_progress' | 'pending_approval' | 'completed';
}

interface MenteeWithActivities extends MenteeWithProgress {
  activities: ActivityWithStatus[];
}

export function MyMenteesTab({ cohortId, mentorId }: MyMenteesTabProps) {
  const [selectedActivity, setSelectedActivity] = useState<ActivityWithStatus | null>(null);
  const [selectedMenteeId, setSelectedMenteeId] = useState<string | null>(null);
  const [selectedMenteeName, setSelectedMenteeName] = useState<string | null>(null);
  const [showReviewModal, setShowReviewModal] = useState(false);
  const [reviewNotes, setReviewNotes] = useState('');
  const [isApproving, setIsApproving] = useState(false);
  
  const queryClient = useQueryClient();
  const { showSuccess, showError } = useNotifications();

  // Fetch mentees
  const {
    data: mentees,
    isLoading: menteesLoading,
    error: menteesError,
  } = useQuery({
    queryKey: queryKeys.mentors.mentees(cohortId, mentorId),
    queryFn: () => mentorsApi.getMentees(cohortId, mentorId),
  });

  // Fetch all activities for the cohort
  const {
    data: activities,
    isLoading: activitiesLoading,
  } = useQuery({
    queryKey: ['activities', cohortId],
    queryFn: () => activitiesApi.list(cohortId),
  });

  // Fetch completions for all mentees using useQueries
  const completionsQueries = useQueries({
    queries: (mentees || []).map(mentee => ({
      queryKey: ['completions', cohortId, mentee.userId],
      queryFn: () => activitiesApi.getUserCompletions(cohortId, mentee.userId),
      enabled: !!mentees && mentees.length > 0,
    })),
  });

  const handleReviewActivity = (activity: ActivityWithStatus, menteeId: string, menteeName: string) => {
    setSelectedActivity(activity);
    setSelectedMenteeId(menteeId);
    setSelectedMenteeName(menteeName);
    setShowReviewModal(true);
  };

  const handleCloseModal = () => {
    setShowReviewModal(false);
    setSelectedActivity(null);
    setSelectedMenteeId(null);
    setSelectedMenteeName(null);
    setReviewNotes('');
  };

  const handleApprove = async () => {
    if (!selectedActivity || !selectedMenteeId) return;

    setIsApproving(true);
    try {
      const activityId = selectedActivity.activityId;
      
      await activitiesApi.approveActivity(
        cohortId,
        activityId,
        selectedMenteeId,
        reviewNotes
      );

      // Refetch data
      await queryClient.invalidateQueries({ queryKey: queryKeys.mentors.mentees(cohortId, mentorId) });
      await queryClient.invalidateQueries({ queryKey: queryKeys.activities.completions(selectedMenteeId, cohortId) });

      showSuccess('Activity approved', `Activity has been approved for ${selectedMenteeName}`);
      handleCloseModal();
    } catch (error) {
      console.error('Error approving activity:', error);
      showError('Approval failed', 'Could not approve activity. Please try again.');
    } finally {
      setIsApproving(false);
    }
  };

  const getStatusIndicator = (status: ActivityWithStatus['status']) => {
    switch (status) {
      case 'completed':
        return <StatusIndicator type="success">Approved</StatusIndicator>;
      case 'pending_approval':
        return <StatusIndicator type="pending">Pending Review</StatusIndicator>;
      case 'in_progress':
        return <StatusIndicator type="in-progress">In Progress</StatusIndicator>;
      case 'not_started':
      default:
        return <StatusIndicator type="stopped">Not Started</StatusIndicator>;
    }
  };

  if (menteesLoading || activitiesLoading) {
    return <LoadingSpinner message="Loading mentees..." />;
  }

  if (menteesError) {
    return <ErrorAlert error={menteesError} />;
  }

  // Combine mentees with their activities
  const menteesWithActivities: MenteeWithActivities[] = (mentees || []).map((mentee, index) => {
    const completions = completionsQueries[index]?.data || [];
    const activitiesWithStatus: ActivityWithStatus[] = (activities || []).map(activity => {
      const activityId = activity.activityId;
      const completion = completions.find(c => c.activityId === activityId);
      let status: ActivityWithStatus['status'] = 'not_started';
      
      if (completion) {
        if (completion.status === 'completed') {
          status = 'completed';
        } else if (completion.status === 'pending_approval') {
          status = 'pending_approval';
        } else {
          status = 'in_progress';
        }
      }

      return {
        ...activity,
        activityId: activityId, // Ensure activityId is set
        completion,
        status,
      };
    });

    return {
      ...mentee,
      activities: activitiesWithStatus,
    };
  });

  return (
    <SpaceBetween size="l">
      {menteesWithActivities.map((mentee) => (
        <Container
          key={mentee.userId}
          header={
            <Header
              variant="h2"
              actions={
                mentee.pendingReviews > 0 ? (
                  <Badge color="red">
                    {mentee.pendingReviews} pending review{mentee.pendingReviews > 1 ? 's' : ''}
                  </Badge>
                ) : undefined
              }
            >
              <UserLink userName={mentee.userName} userId={mentee.userId} />
            </Header>
          }
        >
          <SpaceBetween size="l">
            {/* Progress Bar */}
            <div>
              <Box variant="awsui-key-label" margin={{ bottom: 'xs' }}>
                Progress ({mentee.progress.completed}/{mentee.progress.total})
              </Box>
              <ProgressBar
                value={mentee.progress.percentage}
                status={mentee.progress.percentage === 100 ? 'success' : 'in-progress'}
                resultText={mentee.progress.percentage === 100 ? 'Complete' : undefined}
                variant="standalone"
              />
            </div>

            {/* Activities Table */}
            <Table
              trackBy={(item) => item.activityId || item.title}
              columnDefinitions={[
                {
                  id: 'status',
                  header: 'Status',
                  cell: (item) => getStatusIndicator(item.status),
                  width: 150,
                },
                {
                  id: 'name',
                  header: 'Activity',
                  cell: (item) => item.title,
                  isRowHeader: true,
                },
                {
                  id: 'type',
                  header: 'Type',
                  cell: (item) => <Badge color="blue">{item.activityType}</Badge>,
                  width: 120,
                },
                {
                  id: 'actions',
                  header: 'Actions',
                  cell: (item) =>
                    item.status === 'pending_approval' ? (
                      <Button
                        onClick={() => handleReviewActivity(item, mentee.userId, mentee.userName)}
                      >
                        Review
                      </Button>
                    ) : item.status === 'completed' ? (
                      <Box color="text-status-success">
                        {item.completion?.approvedAt
                          ? `Approved ${new Date(item.completion.approvedAt).toLocaleDateString()}`
                          : 'Approved'}
                      </Box>
                    ) : null,
                  width: 150,
                },
              ]}
              items={mentee.activities}
              empty={
                <Box textAlign="center" color="inherit" padding={{ vertical: 'l' }}>
                  <Box variant="strong">No activities</Box>
                </Box>
              }
              variant="borderless"
            />
          </SpaceBetween>
        </Container>
      ))}

      {menteesWithActivities.length === 0 && (
        <Box textAlign="center" color="text-body-secondary" padding={{ vertical: 'xxl' }}>
          <SpaceBetween size="m">
            <Box variant="h3">No mentees assigned</Box>
            <Box>You don't have any mentees assigned to you yet.</Box>
          </SpaceBetween>
        </Box>
      )}

      {/* Activity Review Modal */}
      <Modal
        visible={showReviewModal}
        onDismiss={handleCloseModal}
        size="medium"
        header={
          <Header variant="h2">
            Review Activity: {selectedActivity?.title}
          </Header>
        }
        footer={
          <Box float="right">
            <SpaceBetween direction="horizontal" size="xs">
              <Button onClick={handleCloseModal} disabled={isApproving}>
                Cancel
              </Button>
              <Button variant="primary" onClick={handleApprove} loading={isApproving}>
                Approve
              </Button>
            </SpaceBetween>
          </Box>
        }
      >
        {selectedActivity && (
          <SpaceBetween size="l">
            <Box>
              <Box variant="awsui-key-label">Submitted by</Box>
              <UserLink userName={selectedMenteeName || ''} userId={selectedMenteeId || ''} />
            </Box>

            {selectedActivity.completion?.submittedAt && (
              <Box>
                <Box variant="awsui-key-label">Submitted</Box>
                {new Date(selectedActivity.completion.submittedAt).toLocaleString()}
              </Box>
            )}

            <Box>
              <Box variant="awsui-key-label">Activity Description</Box>
              <Box>{selectedActivity.description || 'No description provided'}</Box>
            </Box>

            {selectedActivity.completion?.deliverableName && (
              <Box>
                <Box variant="awsui-key-label">Deliverable Name</Box>
                <Box>{selectedActivity.completion.deliverableName}</Box>
              </Box>
            )}

            {selectedActivity.completion?.deliverableLink && (
              <Box>
                <Box variant="awsui-key-label">Deliverable Link</Box>
                <Link external href={selectedActivity.completion.deliverableLink}>
                  {selectedActivity.completion.deliverableLink}
                </Link>
              </Box>
            )}

            {selectedActivity.completion?.recordingLink && (
              <Box>
                <Box variant="awsui-key-label">Recording Link</Box>
                <Link external href={selectedActivity.completion.recordingLink}>
                  {selectedActivity.completion.recordingLink}
                </Link>
              </Box>
            )}

            <FormField
              label="Review Notes (Optional)"
              description="Add any feedback for the participant"
            >
              <Textarea
                value={reviewNotes}
                onChange={({ detail }) => setReviewNotes(detail.value)}
                placeholder="Add any feedback or comments..."
                rows={4}
              />
            </FormField>
          </SpaceBetween>
        )}
      </Modal>
    </SpaceBetween>
  );
}
